import React from 'react';
import { Card, CardContent } from '@/components/ui';

export interface ForemanKpiData {
  today?: { total: number; completed: number; percentage: number };
  weekly?: { total: number; completed: number; percentage: number };
  overdue?: { total: number };
  team?: { total: number; present: number; active: number };
  progress?: { current: number; trend: number };
  safety?: { incidents: number; daysCount: number };
}

interface ForemanKpiGridProps {
  data?: ForemanKpiData;
  isLoading?: boolean;
}

export function ForemanKpiGrid({ data, isLoading }: ForemanKpiGridProps) {
  const stats = {
    today: {
      total: data?.today?.total ?? 7,
      completed: data?.today?.completed ?? 5,
      percentage: data?.today?.percentage ?? 71
    },
    weekly: {
      total: data?.weekly?.total ?? 24,
      completed: data?.weekly?.completed ?? 16,
      percentage: data?.weekly?.percentage ?? 67
    },
    overdue: {
      total: data?.overdue?.total ?? 3
    },
    team: {
      total: data?.team?.total ?? 14,
      present: data?.team?.present ?? 12,
      active: data?.team?.active ?? 10
    },
    progress: {
      current: data?.progress?.current ?? 68,
      trend: data?.progress?.trend ?? 12
    },
    safety: {
      incidents: data?.safety?.incidents ?? 0,
      daysCount: data?.safety?.daysCount ?? 30
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
      <KpiCardWrapper
        title="Công việc hôm nay"
        value={stats.today.total}
        unit="Nhiệm vụ"
        icon="ri-clipboard-line"
        iconBg="bg-emerald-50"
        iconColor="text-emerald-600"
        footer={
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs font-bold">
              <span className="text-emerald-600">{stats.today.completed} đã hoàn thành</span>
              <span className="text-emerald-600">{stats.today.percentage}%</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${stats.today.percentage}%` }} />
            </div>
          </div>
        }
      />

      <KpiCardWrapper
        title="Công việc tuần này"
        value={stats.weekly.total}
        unit="Nhiệm vụ"
        icon="ri-calendar-todo-line"
        iconBg="bg-blue-50"
        iconColor="text-blue-600"
        footer={
          <div className="space-y-1.5">
            <div className="text-xs font-bold text-slate-500">
              Đã hoàn thành {stats.weekly.completed} ({stats.weekly.percentage}%)
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 rounded-full" style={{ width: `${stats.weekly.percentage}%` }} />
            </div>
          </div>
        }
      />

      <KpiCardWrapper
        title="Công việc quá hạn"
        value={stats.overdue.total}
        unit="Nhiệm vụ"
        icon="ri-alarm-warning-line"
        iconBg="bg-orange-50"
        iconColor="text-orange-600"
        footer={
          <div className="flex justify-between items-center">
            <span className="text-xs font-bold text-red-500">Cần xử lý ngay</span>
            <i className="ri-arrow-right-line text-red-500 text-xs" />
          </div>
        }
      />

      <KpiCardWrapper
        title="Nhân sự trong tổ"
        value={`${stats.team.present}/${stats.team.total}`}
        unit="Người"
        icon="ri-group-line"
        iconBg="bg-purple-50"
        iconColor="text-purple-600"
        footer={
          <div className="text-xs font-bold text-emerald-600">
            Có mặt: {stats.team.active}/{stats.team.present}
          </div>
        }
      />

      <KpiCardWrapper
        title="Tiến độ tổng thể"
        value={`${stats.progress.current}%`}
        unit="Xong"
        icon="ri-donut-chart-line"
        iconBg="bg-sky-50"
        iconColor="text-sky-600"
        footer={
          <div className="text-xs font-bold text-emerald-600 flex items-center gap-1">
            <i className="ri-arrow-up-line" />
            {stats.progress.trend}% so với tuần trước
          </div>
        }
      />

      <KpiCardWrapper
        title="An toàn"
        value={stats.safety.incidents}
        unit="Sự cố"
        icon="ri-shield-check-line"
        iconBg="bg-green-50"
        iconColor="text-green-600"
        footer={
          <div className="text-xs font-bold text-slate-400">
            {stats.safety.daysCount} ngày qua
          </div>
        }
      />
    </div>
  );
}

interface WrapperProps {
  title: string;
  value: string | number;
  unit: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  footer: React.ReactNode;
}

function KpiCardWrapper({ title, value, unit, icon, iconBg, iconColor, footer }: WrapperProps) {
  return (
    <Card className="border-none shadow-sm bg-white rounded-md overflow-hidden">
      <CardContent className="p-3">
        <div className="flex gap-2.5 mb-2.5">
          <div className={`w-10 h-10 rounded-md ${iconBg} flex items-center justify-center shrink-0`}>
            <i className={`${icon} ${iconColor} text-lg`} />
          </div>
          <div className="min-w-0">
            <p className="text-xs font-bold text-slate-400 truncate mb-0.5">
              {title}
            </p>
            <div className="flex items-baseline gap-1">
              <span className="text-lg font-black text-slate-900 leading-none">
                {value}
              </span>
              <span className="text-xs font-bold text-slate-500">
                {unit}
              </span>
            </div>
          </div>
        </div>
        <div className="pt-2 border-t border-slate-50">
          {footer}
        </div>
      </CardContent>
    </Card>
  );
}
